import type { Municipality, SearchArea, SpeculatorArea } from "@habity/types";

export const formatRoomRange = (
    rooms_min: number | null,
    rooms_max: number | null
) => {
    if (rooms_min && rooms_max) return `${rooms_min}-${rooms_max} rum`;
    if (rooms_min) return `Från ${rooms_min} rum`;
    if (rooms_max) return `Upp till ${rooms_max} rum`;
    return "";
};

export const formatSpaceRange = (
    living_space_min: number | null,
    living_space_max: number | null
) => {
    if (living_space_min && living_space_max)
        return `${living_space_min}-${living_space_max} m²`;
    if (living_space_min) return `Från ${living_space_min} m²`;
    if (living_space_max) return `Upp till ${living_space_max} m²`;
    return "";
};

export const formatPlotSize = (
    plot_size_min: number | null,
    plot_size_max: number | null
) => {
    if (plot_size_min && plot_size_max)
        return `${plot_size_min}-${plot_size_max} m²`;
    if (plot_size_min) return `Från ${plot_size_min} m²`;
    if (plot_size_max) return `Upp till ${plot_size_max} m²`;
    return "";
};

export const formatPrice = (price: number): string => {
    const million = 1_000_000;

    if (price >= million) {
        const priceInMillions = (price / million).toFixed(1).replace(/\.0$/, ""); // remove .0 for round numbers
        return `${priceInMillions} milj. kr`;
    }

    const formatter = new Intl.NumberFormat("sv-SE", {
        style: "currency",
        currency: "SEK",
        maximumFractionDigits: 0,
    });

    return formatter.format(price);
};

export const formatPriceRange = (
    price_min: number | null,
    price_max: number | null
) => {
    if (price_min && price_max)
        return `${price_min / 1000000}-${price_max / 1000000} mkr`;
    if (price_min) return `Från ${price_min / 1000000} mkr`;
    if (price_max) return `Upp till ${price_max / 1000000} mkr`;
    return "";
};

export const formatMunicipalities = (
    municipalities?: Municipality[] | null
) => {
    return (
        municipalities?.map((municipality) => municipality?.name).join(", ") ||
        "Valfri kommun"
    );
};

export const formatAreas = (areas?: SearchArea[] | null) => {
    return areas?.map((area) => area?.name).join(", ") || "Alla områden";
};

export const formatLocation = (
    areas?: SearchArea[] | null,
    municipalities?: Municipality[] | null
) => {
    const locations: string[] = [];
    if (areas && areas.length > 0) {
        locations.push(formatAreas(areas));
    }
    if (municipalities && municipalities.length > 0) {
        locations.push(formatMunicipalities(municipalities));
    }
    return locations.join(", ");
};

export const formatAreaDisplay = (
    areas?: SpeculatorArea[] | null,
    prioritizedAreaIds?: number[],
    maxVisible = 3,
): string => {
    if (!areas || areas.length === 0) return "hela Sverige";

    const sorted = prioritizedAreaIds?.length
        ? [...areas].sort((a, b) => {
              const aMatch = prioritizedAreaIds.includes(a.id) ? 0 : 1;
              const bMatch = prioritizedAreaIds.includes(b.id) ? 0 : 1;
              return aMatch - bMatch;
          })
        : areas;

    const visible = sorted.slice(0, maxVisible);
    const remaining = sorted.length - maxVisible;

    const areaNames = visible.map((a) => a.name).join(", ");
    const base = areaNames;

    return remaining > 0 ? `${base} och ${remaining} till` : base;
};
